import { ActionConfirmDialog } from "@/components/admin/ActionConfirmDialog";
import { ProtectedActionConfirmDialog } from "@/components/admin/ProtectedActionConfirmDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatAdminLabel } from "@/lib/adminFormatters";
import { hasAdminPermission } from "@/lib/adminPermissions";
import { formatCurrency } from "@/lib/currency";
import { getOrderedModuleCatalog } from "@/lib/moduleDisplayInfo";


const stateTone = {
  locked: "bg-slate-100 text-slate-700",
  purchase_ready: "bg-emerald-50 text-emerald-700",
  access_granted: "bg-emerald-50 text-emerald-700",
  access_under_review: "bg-amber-50 text-amber-700",
  access_revoked: "bg-red-50 text-red-700",
};


export const AdminModuleAccessSection = ({ detail, session, onRunAction }) => {
  const modules = getOrderedModuleCatalog(detail?.module_access || []);
  const canApproveModulePayments = hasAdminPermission(session, "approve_module_payments");
  const canRevokeModuleAccess = session?.role === "super_admin";
  const applicationReady = detail?.application?.payment_status === "paid_confirmed" && detail?.application?.approval_status === "approved" && detail?.application?.overall_status !== "archived";

  return (
    <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid="admin-detail-module-access-card">
      <CardHeader>
        <CardTitle className="text-[1.8rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="admin-detail-module-access-title">Module purchases and access</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm leading-7 text-slate-600" data-testid="admin-detail-module-access-note">Use the module EFT reference below when checking proof of payment in the payments inbox. Access is granted per module only.</p>
        {!applicationReady ? (
          <p className="rounded-[1.15rem] border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-7 text-amber-800" data-testid="admin-detail-module-access-gate-note">
            Module access can only be granted after the application fee is confirmed and the application is approved.
          </p>
        ) : null}
        {!canApproveModulePayments ? (
          <p className="rounded-[1.15rem] border border-stone-200 bg-[#fcfaf5] px-4 py-3 text-sm leading-7 text-slate-700" data-testid="admin-detail-module-access-permission-note">
            Your role can view module access states but cannot approve module payments.
          </p>
        ) : null}

        {modules.map((module) => {
          const actionDisabled = !applicationReady;
          return (
            <div key={module.code} className="rounded-[1.35rem] border border-stone-200 bg-[#fcfaf5] p-5" data-testid={`admin-detail-module-access-item-${module.code.toLowerCase()}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500" data-testid={`admin-detail-module-access-code-${module.code.toLowerCase()}`}>{module.code}</p>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${stateTone[module.access_status] || "bg-slate-100 text-slate-700"}`} data-testid={`admin-detail-module-access-status-${module.code.toLowerCase()}`}>
                      {formatAdminLabel(module.access_status)}
                    </span>
                  </div>
                  <p className="text-base font-semibold text-slate-950" data-testid={`admin-detail-module-access-title-${module.code.toLowerCase()}`}>{module.title}</p>
                  <p className="text-sm text-slate-600" data-testid={`admin-detail-module-access-meta-${module.code.toLowerCase()}`}>Price: {formatCurrency(module.price)} • Reference: {module.payment_reference}</p>
                  {module.updated_at ? <p className="text-xs uppercase tracking-[0.18em] text-slate-500" data-testid={`admin-detail-module-access-updated-${module.code.toLowerCase()}`}>Updated: {new Date(module.updated_at).toLocaleString()}</p> : null}
                </div>

                {canApproveModulePayments ? (
                  <div className="flex flex-wrap gap-3">
                    <ActionConfirmDialog
                      triggerLabel="Grant access"
                      title={`Grant ${module.code} access`}
                      message="Confirm that EFT proof has been reviewed and grant access for this module only."
                      onConfirm={(note) => onRunAction(`/admin/applications/${detail?.application?.id}/modules/${module.code}/grant-access`, `${module.code} access granted.`, "post", note, true)}
                      testIdPrefix={`admin-detail-module-grant-${module.code.toLowerCase()}`}
                      triggerTestId={`admin-detail-module-grant-button-${module.code.toLowerCase()}`}
                      triggerClassName="rounded-full bg-[#1f2d3d] text-white hover:bg-[#15212d]"
                      triggerDisabled={actionDisabled || module.access_status === "access_granted"}
                    />
                    <ActionConfirmDialog
                      triggerLabel="Mark under review"
                      title={`Mark ${module.code} under review`}
                      message="Record that proof of payment needs further review for this module."
                      onConfirm={(note) => onRunAction(`/admin/applications/${detail?.application?.id}/modules/${module.code}/mark-under-review`, `${module.code} marked under review.`, "post", note, true)}
                      testIdPrefix={`admin-detail-module-review-${module.code.toLowerCase()}`}
                      triggerTestId={`admin-detail-module-review-button-${module.code.toLowerCase()}`}
                      triggerVariant="outline"
                      triggerClassName="rounded-full border-[#d7c6a9]"
                      triggerDisabled={actionDisabled || module.access_status === "access_granted" || module.access_status === "access_under_review"}
                    />
                    {canRevokeModuleAccess ? (
                      <ProtectedActionConfirmDialog
                        triggerLabel="Revoke access"
                        title={`Revoke ${module.code} access`}
                        message={`Are you sure you want to revoke access to ${module.code}: ${module.title} for ${detail?.applicant?.first_name} ${detail?.applicant?.surname} / ${detail?.applicant?.student_number}?`}
                        onConfirm={(payload) => onRunAction(`/admin/applications/${detail?.application?.id}/modules/${module.code}/revoke-access`, `${module.code} access revoked.`, "post", payload, true)}
                        testIdPrefix={`admin-detail-module-revoke-${module.code.toLowerCase()}`}
                        triggerTestId={`admin-detail-module-revoke-button-${module.code.toLowerCase()}`}
                        triggerClassName="rounded-full border-red-200 text-red-700 hover:bg-red-50"
                        triggerDisabled={actionDisabled || module.access_status !== "access_granted"}
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>
              {module.access_status === "access_granted" ? <p className="mt-4 text-sm text-emerald-700" data-testid={`admin-detail-module-access-reading-note-${module.code.toLowerCase()}`}>Access granted for this module only. Reader rollout is controlled per module and can still be unavailable while content is being prepared.</p> : null}
              {module.access_status === "access_under_review" ? <p className="mt-4 text-sm text-amber-700" data-testid={`admin-detail-module-access-review-note-${module.code.toLowerCase()}`}>Manual review is still required before access can be granted.</p> : null}
              {module.access_status === "access_revoked" ? <p className="mt-4 text-sm text-red-700" data-testid={`admin-detail-module-access-revoked-note-${module.code.toLowerCase()}`}>Access has been removed for this module only. Super Admin can grant it again if needed.</p> : null}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};